import { IsArray, IsBoolean, IsNumber, IsSanitizedHtml, IsString } from "@wog/mol-lib-api-contract/utils";
import { acpV3PropsMaxLength, acpV3PropsMaxLengthV2 } from "app/common/constant/acp";
import { IsAcpObjectItem } from "app/common/validators/acpObjectItemValidator";
import { IsNotEmptyString } from "app/common/validators/isNotEmptyStringValidator";
import { CustomObject } from "app/modules/acp/hooks/useAcpForm/type";
import { Transform, Type } from "class-transformer";
import {
	ArrayMaxSize,
	ArrayUnique,
	IsDateString,
	IsEnum,
	IsIn,
	IsOptional,
	IsString as IsStringIterable,
	Validate,
	ValidateIf,
	ValidateNested,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from "class-validator";
import { HasHealthConcernValues } from "common/constant/v2/acp";
import { IsNRIC, MaxAppointedNhs } from "common/validators";
import { RELIGIONS } from "server/services/acpV3/AcpPdfDocument/v2/acp/PersonalDetailsCard/type";

//Define enum
export enum FearType {
	PHYSICAL_PAIN = "PHYSICAL_PAIN",
	COST = "COST",
	IMPACT = "IMPACT",
	LOSS_OF_INDEPENDENCE = "LOSS_OF_INDEPENDENCE",
	ALONE = "ALONE",
	OTHERS = "FEAR_OTHERS",
}

enum ConcernType {
	CONCERNS = "CONCERNS",
	FAMILY_MEDICAL_ISSUE = "FAMILY_MEDICAL_ISSUE",
	MEDICAL_CONDITION = "MEDICAL_CONDITION",
	ALLERGY = "ALLERGY",
	PAST_HEALTH_CONDITION = "PAST_HEALTH_CONDITION",
	OTHERS = "OTHERS",
}

enum HasExperienceValues {
	YES = "YES",
	NO = "NO",
}

export enum GoalsOfCare {
	COMFORT_FOCUSED = "COMFORT_FOCUSED",
	LIMITED_INTERVENTION = "LIMITED_INTERVENTION",
	LIFE_SUSTAINING = "LIFE_SUSTAINING",
	UNDECIDED = "UNDECIDED",
}

enum Sources {
	FAMILY = "FAMILY",
	FRIENDS = "FRIENDS",
	LEISURE = "LEISURE",
	RELIGION = "RELIGION",
	OTHERS = "OTHERS",
}

export enum nhsRelationship {
	PARENT = "Parent",
	BROTHER = "Brother",
	SISTER = "Sister",
	SPOUSE = "Spouse",
	SON = "Son",
	SON_IN_LAW = "Son-in-law",
	DAUGHTER = "Daughter",
	DAUGHTER_IN_LAW = "Daughter-in-law",
	GRANDCHILD = "Grandchild",
	OTHER = "Other",
}

//Define custom constrains and decorator
@ValidatorConstraint({ name: "customText", async: false })
class NhsCustomTextLength implements ValidatorConstraintInterface {
	validate(value: string, args: ValidationArguments) {
		const fearsType = args.object["fearType"];
		if (fearsType === FearType.OTHERS && value.length > acpV3PropsMaxLength.fears.others.description) {
			return false;
		}
		if (fearsType !== FearType.OTHERS && value.length > acpV3PropsMaxLength.fears.description) {
			return false;
		}
		return true;
	}

	defaultMessage(args: ValidationArguments) {
		return `${args.property} value must be less than ${
			args.object["fearType"] === FearType.OTHERS
				? acpV3PropsMaxLength.fears.others.description
				: acpV3PropsMaxLength.fears.description
		} char.`;
	}
}

@ValidatorConstraint({ name: "validOtherRelationship", async: false })
class ValidOtherRelationship implements ValidatorConstraintInterface {
	validate(value: any, args: ValidationArguments) {
		const object = args.object as NhsDetailsDto;
		if (value && object.relationship !== nhsRelationship.OTHER) {
			return false;
		}
		return true; // valid in all other cases
	}

	defaultMessage() {
		return `relationship value must be Other if otherRelationship has value.`;
	}
}

//details props type defines
export class ConcernsDto {
	@IsEnum(ConcernType)
	concernType!: ConcernType;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.healthConcerns.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsBoolean()
	isSelected?: boolean;
}

export class FearsItemDto {
	@IsEnum(FearType)
	fearType!: FearType;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.fears.others.title })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	othersType?: string;

	@Validate(NhsCustomTextLength)
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsBoolean({ isOptional: true })
	isSelectedOther?: boolean;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	id?: string;
}

export class TopFearsDto {
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	label?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	value?: string;
}

export class NhsDetailsDto {
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	name?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	dob?: string;

	@IsEnum(nhsRelationship)
	@ValidateIf((nhsDetail) => nhsDetail.relationship !== "")
	relationship?: nhsRelationship;

	@IsString({ maxLength: acpV3PropsMaxLength.nhs.email })
	@IsOptional()
	@ValidateIf((nhsDetail) => nhsDetail.email !== "")
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	email?: string;

	@Validate(ValidOtherRelationship)
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	otherRelationship?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.nhs.mobile })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	mobile?: string;
}

export class NhsDetailsDtoV2 {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.name })
	name!: string;

	@IsDateString()
	dob!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.email })
	email!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.mobile })
	mobile!: string;

	@IsEnum(nhsRelationship)
	@ValidateIf((nhsDetail) => nhsDetail.relationship !== "")
	relationship?: nhsRelationship;

	@Validate(ValidOtherRelationship)
	@IsString({ isOptional: true })
	otherRelationship?: string;

	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	@IsNRIC()
	nric!: string;
}

export class NhsDraftDetailsDtoV2 {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.nhs.name })
	name!: string;

	@IsOptional()
	@Transform(({ value }) => (value === "" ? undefined : value))
	@IsDateString()
	dob!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.nhs.email })
	email!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.nhs.mobile })
	mobile!: string;

	@IsOptional()
	@Transform(({ value }) => (value === "" ? undefined : value))
	@IsEnum(nhsRelationship)
	@ValidateIf((nhsDetail) => nhsDetail.relationship !== "")
	relationship?: nhsRelationship;

	@IsOptional()
	@Transform(({ value }) => (value === "" ? undefined : value))
	@IsString()
	otherRelationship?: string;

	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.nhs.nric })
	nric!: string;
}

export class CustomInputObject {
	[key: string]: string | string[] | CustomObject;
}

//props defines
export class FearsDto {
	@ValidateNested({ each: true, always: true })
	@Type(() => FearsItemDto)
	fears!: FearsItemDto[];

	@ValidateNested({ each: true, always: true })
	@ArrayMaxSize(acpV3PropsMaxLength.fears.topFears)
	@Type(() => TopFearsDto)
	ranking!: TopFearsDto[];
}

export class FearsDtoV2 {
	@ValidateNested({ each: true, always: true })
	@Type(() => FearsItemDto)
	fears!: FearsItemDto[];
}

export class HealthConcernsDto {
	@ValidateIf((healthConcerns) => healthConcerns.isConcerned !== "")
	@IsEnum(HasHealthConcernValues)
	isConcerned!: HasHealthConcernValues;

	@ValidateNested({ each: true, always: true })
	@Type(() => ConcernsDto)
	concerns!: ConcernsDto[];
}

export class NhsDto {
	@ValidateNested({ each: true, always: true })
	@Type(() => NhsDetailsDto)
	spokesPersons!: NhsDetailsDto[];
}

export class NhsDtoV2 {
	@IsOptional()
	@ValidateNested({ each: true, always: true })
	@Type(() => NhsDetailsDtoV2)
	@MaxAppointedNhs({ always: true })
	@ArrayUnique((person: NhsDetailsDtoV2) => person.nric)
	spokesPersons!: NhsDetailsDtoV2[];
}

export class NhsDraftDtoV2 {
	@IsOptional()
	@ValidateNested({ each: true, always: true })
	@Type(() => NhsDraftDetailsDtoV2)
	@MaxAppointedNhs({ always: true })
	@ArrayUnique((person: NhsDraftDetailsDtoV2) => person.nric)
	spokesPersons!: NhsDraftDetailsDtoV2[];
}

export class NhsAdditionalRequestDto {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.nhsAdditionalRequests.request })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	request?: string;
}

export class PersonalDetailsDto {
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	name?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	uinfin?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	dob?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	sex?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.personalDetails.mobileNo })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	mobileNo?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.personalDetails.email })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	email?: string;
}

export class RegisteredAddress {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.block })
	block!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.street })
	street!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.level })
	floor!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.unit })
	unit!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.postalCode })
	postal!: string;
}

export class PersonalDetailsDtoV2 {
	@IsNotEmptyString()
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.name })
	name!: string;

	@IsNRIC()
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.nric })
	uinfin!: string;

	@IsDateString()
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.dob })
	dob!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.gender })
	sex!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.phone })
	@IsOptional()
	mobileNo!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.personalDetails.email })
	@IsOptional()
	email?: string;

	// v2 additional fields
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.race })
	race!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.maritalStatus })
	marital!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.citizenship })
	residentialStatus!: string;

	@IsString({ isOptional: true })
	passtype?: string;

	// TODO: Remove RegisteredAddress from src/app/modules/acp/hooks/useAcpForm/type.ts later
	@ValidateNested({ each: true })
	@Type(() => RegisteredAddress)
	registeredAddress!: RegisteredAddress;

	// TODO: Should we move this enum to ACP enum file?
	// TODO: ValidateIf + IsOptional does not affect @IsIn. Fix this later
	@IsOptional()
	@IsIn([...Object.keys(RELIGIONS), ""])
	religion?: string;
}

export class PersonalExperiencesDto {
	@ValidateIf((personalExperiences) => personalExperiences.hasExperiences !== "")
	@IsEnum(HasExperienceValues)
	hasExperiences!: HasExperienceValues;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.personalExperiences.statement })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	statement?: string;
}

export class PersonalExperiencesDtoV2 {
	@ValidateIf((personalExperiences) => personalExperiences.hasExperiences !== "")
	@IsEnum(HasExperienceValues)
	hasExperiences!: HasExperienceValues;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.personalExperience.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	statement?: string;
}

export class SourcesOfSupportDto {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	sources!: string[];

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.sourceOfSupport.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsAcpObjectItem(acpV3PropsMaxLength.sourceOfSupport.otherSource)
	@Type(() => CustomInputObject)
	@IsOptional()
	@ValidateIf((sourcesOfSupport) => sourcesOfSupport.otherSources !== "")
	otherSources?: CustomInputObject;
}

export class SourcesOfSupportDtoV2 {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsEnum(Sources, { each: true })
	sources!: string[];

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.sourceOfSupport.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsString({ maxLength: acpV3PropsMaxLength.sourceOfSupport.otherSource.OTHER_SOURCES })
	@ValidateIf((sourcesOfSupport) => sourcesOfSupport.otherSources !== "")
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	otherSources?: string;
}

export class ThingsYouEnjoyInLifeDto {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	selections?: string[];

	@IsAcpObjectItem(acpV3PropsMaxLength.thingsYouEnjoyInLife)
	@Type(() => CustomInputObject)
	@IsOptional()
	@ValidateIf((thingsYouEnjoyInLife) => thingsYouEnjoyInLife.selectionsDetail !== "")
	selectionsDetail?: CustomInputObject;
}

export class ThingsYouEnjoyInLifeDtoV2 {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	selections?: string[];

	@IsAcpObjectItem(acpV3PropsMaxLengthV2.thingsYouEnjoyInLife)
	@Type(() => CustomInputObject)
	@IsOptional()
	@ValidateIf((thingsYouEnjoyInLife) => thingsYouEnjoyInLife.selectionsDetail !== "")
	selectionsDetail?: CustomInputObject;
}

export class CarePreferencesDto {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	selections?: string[];

	@IsAcpObjectItem(acpV3PropsMaxLength.carePreferences)
	@Type(() => CustomInputObject)
	@IsOptional()
	@ValidateIf((carePreference) => carePreference.selectionsDetail !== "")
	selectionsDetail?: CustomInputObject;
}

export class GoalsOfCareDto {
	@ValidateIf((goalsOfCare) => goalsOfCare.goalsOfCare !== "")
	@IsEnum(GoalsOfCare)
	goalsOfCare?: GoalsOfCare;

	@IsString({ isOptional: true, maxLength: 250 })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsString({ isOptional: true, maxLength: 250 })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	outcomesToAvoid?: string;
}

export class GoalsOfCareDtoV2 {
	@ValidateIf((goalsOfCare) => goalsOfCare.goalsOfCare !== "")
	@IsEnum(GoalsOfCare)
	goalsOfCare?: GoalsOfCare;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.goalsOfCare.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.goalsOfCare.procedureToAvoid })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	outcomesToAvoid?: string;
}

export class NhsAdditionalRequestSubmitDTOV2 {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.nhsAdditionalRequests.request })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	request?: string;
}

export class DraftContentDto {
	@IsNumber()
	currentStep!: number;

	@IsNumber()
	latestStep!: number;

	@IsBoolean()
	suitabilityCheck!: boolean;

	@ValidateNested({ each: true })
	@Type(() => FearsDto)
	@IsOptional()
	fears?: FearsDto;

	@ValidateNested({ each: true })
	@Type(() => HealthConcernsDto)
	@IsOptional()
	healthConcerns?: HealthConcernsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsDto)
	@IsOptional()
	nhs?: NhsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsAdditionalRequestDto)
	@IsOptional()
	nhsAdditionalRequests?: NhsAdditionalRequestDto;

	@ValidateNested({ each: true })
	@Type(() => PersonalDetailsDto)
	@IsOptional()
	personalDetails?: PersonalDetailsDto;

	@ValidateNested({ each: true })
	@Type(() => PersonalExperiencesDto)
	@IsOptional()
	personalExperiences?: PersonalExperiencesDto;

	@ValidateNested({ each: true })
	@Type(() => SourcesOfSupportDto)
	@IsOptional()
	sourcesOfSupport?: SourcesOfSupportDto;

	@ValidateNested({ each: true })
	@Type(() => ThingsYouEnjoyInLifeDto)
	@IsOptional()
	thingsYouEnjoyInLife?: ThingsYouEnjoyInLifeDto;

	@ValidateNested({ each: true })
	@Type(() => GoalsOfCareDto)
	@IsOptional()
	goalsOfCare?: GoalsOfCareDto;

	@ValidateNested({ each: true })
	@Type(() => CarePreferencesDto)
	@IsOptional()
	carePreferences?: CarePreferencesDto;
}

// TODO: Add max validation for currentStep + latestStep
export class DraftContentDtoV2 {
	@IsNumber({ min: 0 })
	currentStep!: number;

	@IsNumber({ min: 0 })
	latestStep!: number;

	@IsBoolean()
	suitabilityCheck!: boolean;

	@ValidateNested({ each: true })
	@Type(() => FearsDtoV2)
	@IsOptional()
	fears?: FearsDtoV2;

	@ValidateNested({ each: true })
	@Type(() => HealthConcernsDto)
	@IsOptional()
	healthConcerns?: HealthConcernsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsDraftDtoV2)
	@IsOptional()
	nhs?: NhsDraftDtoV2;

	@ValidateNested({ each: true })
	@Type(() => NhsAdditionalRequestSubmitDTOV2)
	@IsOptional()
	nhsAdditionalRequests?: NhsAdditionalRequestSubmitDTOV2;

	@ValidateNested({ each: true })
	@Type(() => PersonalDetailsDtoV2)
	@IsOptional()
	personalDetails?: PersonalDetailsDtoV2;

	@ValidateNested({ each: true })
	@Type(() => PersonalExperiencesDtoV2)
	@IsOptional()
	personalExperiences?: PersonalExperiencesDtoV2;

	@ValidateNested({ each: true })
	@Type(() => SourcesOfSupportDtoV2)
	@IsOptional()
	sourcesOfSupport?: SourcesOfSupportDtoV2;

	@ValidateNested({ each: true })
	@Type(() => ThingsYouEnjoyInLifeDtoV2)
	@IsOptional()
	thingsYouEnjoyInLife?: ThingsYouEnjoyInLifeDtoV2;

	@ValidateNested({ each: true })
	@Type(() => GoalsOfCareDtoV2)
	@IsOptional()
	goalsOfCare?: GoalsOfCareDtoV2;

	@ValidateNested({ each: true })
	@Type(() => CarePreferencesDto)
	@IsOptional()
	carePreferences?: CarePreferencesDto;
}

export class PdfDto {
	@IsString()
	date!: string;

	@IsNumber()
	currentStep!: number;

	@ValidateNested({ each: true })
	@Type(() => FearsDto)
	@IsOptional()
	fears?: FearsDto;

	@ValidateNested({ each: true })
	@Type(() => HealthConcernsDto)
	@IsOptional()
	healthConcerns?: HealthConcernsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsDto)
	@IsOptional()
	nhs?: NhsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsAdditionalRequestDto)
	@IsOptional()
	nhsAdditionalRequest?: NhsAdditionalRequestDto;

	@ValidateNested({ each: true })
	@Type(() => PersonalDetailsDto)
	@IsOptional()
	personalDetails?: PersonalDetailsDto;

	@ValidateNested({ each: true })
	@Type(() => PersonalExperiencesDto)
	@IsOptional()
	personalExperiences?: PersonalExperiencesDto;

	@ValidateNested({ each: true })
	@Type(() => SourcesOfSupportDto)
	@IsOptional()
	sourcesOfSupport?: SourcesOfSupportDto;

	@ValidateNested({ each: true })
	@Type(() => ThingsYouEnjoyInLifeDto)
	@IsOptional()
	thingsYouEnjoyInLife?: ThingsYouEnjoyInLifeDto;

	@ValidateNested({ each: true })
	@Type(() => GoalsOfCareDto)
	@IsOptional()
	goalsOfCare?: GoalsOfCareDto;

	@ValidateNested({ each: true })
	@Type(() => CarePreferencesDto)
	@IsOptional()
	carePreferences?: CarePreferencesDto;
}

export class PdfDtoV2 {
	@IsString()
	date!: string;

	@IsNumber()
	currentStep!: number;

	@ValidateNested({ each: true })
	@Type(() => SourcesOfSupportDtoV2)
	@IsOptional()
	sourcesOfSupport?: SourcesOfSupportDtoV2;

	@ValidateNested({ each: true })
	@Type(() => NhsDtoV2)
	@IsOptional()
	nhs?: NhsDtoV2;

	// [TO DO] update this once its finalised. for now added to remove build error in acpv3service.ts
	@ValidateNested({ each: true })
	@IsOptional()
	personalDetails?: any;

	@ValidateNested({ each: true })
	@IsOptional()
	nhsAdditionalRequest?: any;

	@ValidateNested({ each: true })
	@IsOptional()
	personalExperiences?: any;

	@ValidateNested({ each: true })
	@IsOptional()
	thingsYouEnjoyInLife?: any;

	@ValidateNested({ each: true })
	@IsOptional()
	goalsOfCare?: any;

	@ValidateNested({ each: true })
	@IsOptional()
	carePreferences?: any;

	@ValidateNested({ each: true })
	@IsOptional()
	fears?: any;

	@ValidateNested({ each: true })
	@IsOptional()
	healthConcerns?: any;
}
